import type { CurrencyInfo, SelectInfo } from '~/api/interface/mall'

/**
 * 货币相关
 * @description: 赋值逻辑：ssr阶段从链接参数获取platformCurrency，否则从cookie获取，赋值给currentCurrency；
 * 调接口获取货币列表，查看当前货币是否非法，非法则取默认货币赋值给currentCurrency；
 * csr阶段，忽视链接参数，直接从cookie获取currentCurrency；
 */
export const useCurrencyStore = defineStore(
  'currency',
  () => {
    // const { defaultCurrency, filterCurrencyList } = useProjectRuntimeConfig()

    // TODO: 狂刷拿不到nuxtapp实例导致报错getCacheKeyPrefix-err。同时由于没有别的第三方平台了，所以可以写死，具体看useProjectRuntimeConfig的defaultCurrency和filterCurrencyList
    const defaultCurrency = ''
    const filterCurrencyList: string | string[] = []
    const currencyList = ref<CurrencyInfo[]>([])

    const currency = useCookie<{ currentCurrency: string }>('currency', {
      maxAge: 60 * 60 * 24 * 365,
      default: () => {
        return {
          currentCurrency: defaultCurrency || '',
        }
      },
    })

    // currency.value = currency.value || {
    //   currentCurrency: defaultCurrency || '',
    // }
    const currentCurrency = computed({
      get: () => currency.value?.currentCurrency || '',
      set: value => currency.value.currentCurrency = value,
    })

    /** 设置当前货币单位 */
    function setCurrentCurrency(currency: string) {
      currentCurrency.value = currency
    }

    /** 获取初始选中值 */
    const getInitialValue = <U extends keyof T, T extends { select: boolean }>({
      list,
      key,
      value,
    }: {
      list: MaybeRef<T[]>
      key: U
      value: any
    }) => {
      const isMatched = value && (unref(list).findIndex(item => item[key] === value) >= 0)
      if (!isMatched) {
        /** 没有数据，拿接口默认值 */
        value = unref(list).find(({ select }) => select)?.[key] || ''
      }
      return value
    }

    const initCurrencyData = (list: CurrencyInfo[]) => {
      list = list.map(item => ({ ...item, currencyLogo: `${item?.currencyLogo} ${item.currency}` }))
      currencyList.value = (filterCurrencyList.length === 0 ? list : list.filter(e => filterCurrencyList.includes(e.currency))).map(item => ({ ...item, disabled: false }))
      const value = currentCurrency.value
      const finalCurrency = getInitialValue({
        list: currencyList,
        key: 'currency',
        value,
      })
      setCurrentCurrency(finalCurrency)
    }

    const initSelectData = (selectInfo: SelectInfo) => {
      const { currencyList } = selectInfo
      initCurrencyData(currencyList)
    }

    /** 设置货币列表禁用选项 */
    const setCurrencyListDisableOption = (showList: string[]) => {
      const currencyObj: Record<string, true> = {}
      showList.reduce((obj, item) => {
        obj[item] = true
        return obj
      }, currencyObj)
      currencyList.value = currencyList.value.map(item => ({
        ...item,
        disabled: !currencyObj[item.currency],
      }))
    }
    /** 取消货币列表禁用选项 */
    const cancelCurrencyListDisableOption = () => {
      currencyList.value = currencyList.value.map(item => ({
        ...item,
        disabled: false,
      }))
    }

    // 判断服务端缓存中硬编码的货币列表是否变化了，变了就控制台提示一下
    if (process.client) {
      setTimeout(() => judgeCacheCurrencyListForCSR(currencyList.value.map(item => item.currency)))
    }

    return {
      currencyList,
      currentCurrency,
      initSelectData,
      initCurrencyData,
      setCurrentCurrency,
      cancelCurrencyListDisableOption,
      setCurrencyListDisableOption,
    }
  },
  {
    // https://prazdevs.github.io/pinia-plugin-persistedstate/frameworks/nuxt-3.html
    persist: false,
  },
)

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(userStore, import.meta.hot))
}
